// Utility to fetch service keys from database via GraphQL API

import { graphqlClient, extractData } from '../services/graphql/client';
import { SERVICE_PROVIDER_BY_KEY_QUERY } from '../services/graphql/queries';
import { ServiceConfig, serviceConfigurations } from './serviceKeys';

/**
 * Interface for service provider data returned by GraphQL
 */
interface GraphQLServiceProvider {
  id: string;
  serviceProviderName: string;
  serviceKey: string;
  contactEmail: string;
  contactPhone?: string;
  primaryColor: string;
  logoUrl?: string;
  isActive: boolean;
  maxDistance: number;
  defaultLocation?: { lat: number; lng: number };
  defaultZoom: number;
  timezone: string;
  attributesJson?: string;
}

// In-memory cache for fetched service configs
const serviceConfigCache: Record<string, { config: ServiceConfig; fetchedAt: number }> = {};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Convert a GraphQL service provider response to a ServiceConfig
 */
function convertToServiceConfig(provider: GraphQLServiceProvider): ServiceConfig {
  const attributes = provider.attributesJson
    ? JSON.parse(provider.attributesJson)
    : undefined;

  return {
    serviceId: provider.serviceKey,
    serviceName: provider.serviceProviderName,
    primaryColor: provider.primaryColor,
    logoUrl: provider.logoUrl,
    maxDistance: provider.maxDistance,
    defaultLocation: provider.defaultLocation,
    defaultZoom: provider.defaultZoom,
    contactInfo: {
      phone: provider.contactPhone,
      email: provider.contactEmail,
    },
    timezone: provider.timezone,
    attributes,
  };
}

/**
 * Fetch a single service provider config by key from the GraphQL API.
 * Returns null if not found or on error.
 * Uses in-memory cache with 5-minute TTL.
 */
export async function fetchServiceConfigByKey(serviceKey: string): Promise<ServiceConfig | null> {
  // Check hardcoded configs first
  if (serviceConfigurations[serviceKey]) {
    return serviceConfigurations[serviceKey];
  }

  // Check cache
  const cached = serviceConfigCache[serviceKey];
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.config;
  }

  try {
    const response = await graphqlClient.query<{ serviceProviderByKey: GraphQLServiceProvider | null }>(
      SERVICE_PROVIDER_BY_KEY_QUERY,
      { serviceKey }
    );

    const { data, error } = extractData(response);
    if (error || !data?.serviceProviderByKey) {
      console.warn(`[dbServiceKeys] Service key "${serviceKey}" not found in API:`, error);
      return null;
    }

    const config = convertToServiceConfig(data.serviceProviderByKey);
    serviceConfigCache[serviceKey] = { config, fetchedAt: Date.now() };
    return config;
  } catch (err) {
    console.error('[dbServiceKeys] Failed to fetch service config:', err);
    return null;
  }
}
